import type { OperatingSystem } from "./OperatingSystem";
import getUserAgent from "./getUserAgent";

type Browser = "GrailedWebView" | "Chrome" | "Edge";

/**
 * @private
 * @description A mapping of operating systems to regexes that can be used to test user agent
 * strings to see if the user agent is from the operating system.
 */
const OPERATING_SYSTEM_USER_AGENT_REGEXES: Record<OperatingSystem, RegExp> = {
  iOS: /iPhone|iPod|iOS/i,
  Android: /Android/i,
};

/**
 * @private
 * @name Navigator.isOperatingSystem
 * @description Given an Operating System and a User Agent, returns true if the given Operating
 * System is the current Operating System, or false if it isn't.
 */
const isOperatingSystem =
  (os: OperatingSystem) =>
  (userAgent: string): boolean =>
    OPERATING_SYSTEM_USER_AGENT_REGEXES[os].test(userAgent);

const isIOSUserAgent = isOperatingSystem("iOS");

/**
 * @name Navigator.isIOS
 * @description Returns true if the given User Agent's Operating System is iOS, otherwise returns
 * false. Defaults to the current User Agent.
 */
export const isIOS = (userAgent: string = getUserAgent()): boolean =>
  isIOSUserAgent(userAgent);

/**
 * @private
 * @description A mapping of browsers to regexes that can be used to test user agent strings to see
 * if the user agent is from the browser.
 */
const BROWSER_USER_AGENT_REGEXES: Record<Browser, RegExp> = {
  GrailedWebView: /Grailed/i,
  Chrome: /Chrome/i,
  Edge: /Edge/i,
};

/**
 * @private
 * @name Navigator.isBrowser
 * @description Given a Browser and a User Agent, returns true if the given Browser is the current
 * Browser, or false if it isn't.
 */
const isBrowser =
  (browser: Browser) =>
  (userAgent: string): boolean =>
    BROWSER_USER_AGENT_REGEXES[browser].test(userAgent);

/**
 * @name Navigator.isGrailedWebView
 * @description Given a User Agent, returns true if the current Browser is a Grailed Web View,
 * otherwise returns false.
 */
const isGrailedWebView = isBrowser("GrailedWebView");

/**
 * @name Navigator.isWebView
 * @description Given a User Agent, returns true if the current Browser is an app's Web View
 */
const isWebView = (userAgent: string): boolean => isGrailedWebView(userAgent);

/**
 * @name Navigator.isIOSApp
 * @description Returns true if the given User Agent belongs to an iOS Web View. Defaults to the
 * current User Agent.
 */
export const isIOSApp = (userAgent: string = getUserAgent()): boolean =>
  isIOS(userAgent) && isWebView(userAgent);
